import { DatasetDetail } from './dataset/detail';
import { NewDataset } from './dataset/new-dataset';
import { V0Layout } from './components/inner-layout';
import { useAppData, useTenantedClient, useWhoami } from '../app/stores/org';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Center,
  DisplayError,
  Loading,
  ProtectedRoute,
  toast,
  useModalManager,
} from '../app/components';
import { Text } from '@cotera/client/app/components/ui';
import { Actions } from './actions/framework/context';
import { RunDatasetChangesAction } from './dataset/actions/run-changes.action';
import { CommandPalette } from './actions/command-palette/command-palette';
import { NewDatasetAction } from './dataset/actions/new-dataset.action';
import { PublishGraphAction } from './graph/publish.action';
import { ClearChangesAction } from './graph/clear-changes.action';
import { SaveDatasetAction } from './dataset/actions/save-dataset.action';
import { OpenEditColumnAction } from './dataset/actions/open-edit-column.action';
import { RenameDatasetAction } from './dataset/actions/rename-dataset-operation';
import { FormatAction } from './dataset/actions/format.action';
import { CreateChildAction } from './dataset/actions/create-child.action';
import { DeleteDatasetAction } from './dataset/actions/delete-dataset.action';
import { RunOnSourceAction } from './dataset/actions/run-on-source.action';
import { TabManager, useTabManager } from './tab-manager/tab-manager';
import { ToggleEditorAction } from './dataset/actions/toggle-editor.action';
import { NewColumnAction } from './dataset/actions/new-column.action';
import { NewLlmColumn } from './columns/llm/llm.edit';
import { LLMColumnFilterPreviewAction } from './columns/llm/actions/llm-column-preview.action';
import { NewColumn } from './columns/new-column';
import { CloseTabAction } from './actions/tabs/close-tab.action';
import { SaveLlmColumnAction } from './columns/llm/actions/save-llm-column.action';
import { NewExpressionColumn } from './columns/expression/expression.edit';
import { RunPreviewExpressionColumnAction } from './columns/expression/actions/run-expression-column-preview.action';
import { EditColumnRouter } from './columns/edit-column-router';
import { SaveExpressionColumnAction } from './columns/expression/actions/save-expression-column.action';
import { OperationsPage } from './operations.tab';
import { IdentitesPage } from './identities.tab';
import { DeleteColumnAction } from './columns/common-actions/delete-column.action';
import { SetPrimaryKeyAction } from './dataset/actions/set-pk.action';
import {
  UrlTabManagerStorageAdapter,
  useStorageAdapter,
} from './tab-manager/storage';
import { NewTaxonomyColumn } from './columns/taxonomy/edit';
import { DocumentTab, NewDocumentTab } from './document/document.view';
import { NewDocumentAction } from './document/new-document.action';
import { SaveDocumentAction } from './document/save-document.action';
import { ToolTab, NewToolTab } from './tool/tool.view';
import { NewToolAction } from './tool/new-tool.action';
import { RenameToolAction } from './tool/rename-tool.action';
import { DeleteToolAction } from './tool/delete-tool.action';
import { SaveToolAction } from './tool/save-tool.action';
import { SelectToolAction } from './tool/select-tool.action';
import { UpdateCredentialsAction } from './tool/update-credentials.action';
import { AgentTab, NewAgentTab } from './agent/agent.view';
import { NewAgentAction } from './agent/new-agent.action';
import { SaveAgentAction } from './agent/save-agent.action';
import { DeleteAgentAction } from './agent/delete-agent.action';
import { RenameAgentAction } from './agent/rename-agent.action';
import { ChangeAgentTabAction } from './agent/change-agent-tab.action';
import { ApplyTemplateAction } from './agent/apply-template.action';
import { ScheduleAgentAction } from './agent/schedule-agent.action';
import { ChangeAgentModelAction } from './agent/change-agent-model.action';
import { NavigateToMentionAction } from './graph/navigate-to-mention.action';
import { VoiceTranscriptionCompleteAction } from './agent/voice-transcription.action';
import { SubmitChatMessageAction } from './agent/submit-chat-message.action';
import { ClearChatAction } from './agent/clear-chat.action';
import { SaveTaxonomyColumnAction } from './columns/taxonomy/actions/save-taxonomy-column.action';
import { RunPreviewTaxonomyColumnAction } from './columns/taxonomy/actions/run-taxonomy-column-preview.action';
import { LLMColumnSnapshotAction } from './columns/llm/actions/llm-column-snapshot.action';
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  DisableAutoPublishAction,
  EnableAutoPublishAction,
} from './graph/toggle-auto-publish.action';
import { DefinitionsPage } from './definitions/view';
import { ClearAllFiltersAction } from './data-grid/actions/clear-all-filters.actions';
import { RenameColumnAction } from './columns/common-actions/rename-column.action';
import { LlmRunFromStartAction } from './columns/llm/actions/run-from-start.action';
import { AutoPublishAction } from './graph/auto-publish.action';
import { CreateViewAction } from './dataset/actions/create-view.action';
import { DownloadCsvAction } from './data-grid/actions/download-csv.action';
import { NewToolCallColumn } from './columns/tool-call/tool-call.edit';
import { ToolCallColumnPreviewAction } from './columns/tool-call/actions/run-tool-call-column-preview.action';
import { AtcVisibilityPage } from './pages/atc-visibility';
import { BillEngineeringHoursPage } from './pages/bill-engineering-hours';
import { SaveToolCallColumnAction } from './columns/tool-call/actions/save-tool-call-column.action';
import { ToolCallColumnSnapshotAction } from './columns/tool-call/actions/tool-call-column-snapshot.action';
import { ToolCallRunFromStartAction } from './columns/tool-call/actions/run-from-start.action';
import { RunHistoryPage } from './activity';
import { RunPage } from './activity/run-activity.page';
import { UnauthorizedPage } from '../app/pages/unauthorised';
import { MaterializationIde } from '../app/pages/ide/materialization';
import { Artifacts } from '../app/pages/ide/artifacts/list';
import { ViewArtifact } from '../app/pages/ide/artifacts/view-artifact';
import { RenameDocumentAction } from './document/rename-document.action';
import { DeleteDocumentAction } from './document/delete-document.action';
import { DuplicateAction } from './graph/duplicate.action';
import { HomePageFallbackNewUser } from './pages/homepage-fallback-new-user';
import { HomePageFallbackNormal } from './pages/homepage-fallback-normal';
import {
  BaseModel,
  useInScopeModel,
  useWatchableValue,
  Watchable,
} from './actions/framework';
import { GraphModel } from './graph/graph.model';
import { buildDatasetTree } from './components/sidebar/tree-builder';
import {
  SubmitToolResultsAction,
  ExecuteToolAction,
  ExecuteAllIncompleteToolCallsAction,
  ReplayAction,
  EditAction,
  StopAction,
  OpenChatHistoryAction,
} from './chat/actions';
import { ActionRouteRunner } from './actions/action-route';
import { LlmRunColumnAction } from './columns/llm/actions/run-llm-column.action';
import { ToolCallRunColumnAction } from './columns/tool-call/actions/run-tool-call-column.action';
import { ConvertToV2Action } from './columns/llm/actions/convert-to-v2.action';
import { RunAllPreviewsAction } from './columns/llm/actions/run-all-previews.action';
import { CsvUploadAction } from './dataset/csv-upload/csv-upload.action';
import { ConfigureTableAction } from './dataset/csv-upload/configure-table.action';
import { AppTab } from './components/tab/tab';
import { RunAtcCellAction } from './data-grid/atc/run-atc-cell.action';
import { OpenBillingSettings } from './actions/tabs/open-billing-settings.action';
import { OpenSettings } from './actions/tabs/open-settings.action';

const LoadingGraph: React.FC = () => {
  return (
    <div className="h-full w-full flex items-center justify-center flex-col gap-2">
      <Loading.Dots />
    </div>
  );
};

const isOnRoutePage = (location: { pathname: string }, ROOT_URL: string) => {
  // Only match exactly '/', ignore query params
  return location.pathname === ROOT_URL;
};

export const V0TabManager: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const tabManager = useStorageAdapter(
    new UrlTabManagerStorageAdapter('tabs', window)
  );

  const navigate = useNavigate();
  const location = useLocation();

  const org = useWhoami((s) => s.org);
  const ROOT_URL = `/${org.slug}/`;

  return (
    <TabManager
      storageAdapter={tabManager}
      beforeNavigate={() => {
        if (!isOnRoutePage(location, ROOT_URL)) {
          navigate(ROOT_URL);
        }
      }}
      tabConfigs={[
        {
          path: '/home',
          element: <HomePageTab />,
        },
        {
          path: '/dataset',
          children: [
            { path: 'new/:id', element: <NewDataset /> },
            {
              path: ':id',
              element: <DatasetDetail />,
              children: [
                {
                  path: 'column',
                  children: [
                    {
                      path: 'new',
                      element: <NewColumn />,
                      children: [
                        { path: 'llm', element: <NewLlmColumn /> },
                        {
                          path: 'expression',
                          element: <NewExpressionColumn />,
                        },
                        {
                          path: 'taxonomy',
                          element: <NewTaxonomyColumn />,
                        },
                        {
                          path: 'tool-call',
                          element: <NewToolCallColumn />,
                        },
                      ],
                    },
                    {
                      path: ':columnId',
                      element: <EditColumnRouter />,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '/document',
          children: [
            {
              path: 'new/:id',
              element: <NewDocumentTab />,
            },
            {
              path: ':id',
              element: <DocumentTab />,
            },
          ],
        },
        {
          path: '/tool',
          children: [
            {
              path: 'new/:id',
              element: <NewToolTab />,
            },
            {
              path: ':id',
              element: <ToolTab />,
            },
          ],
        },
        {
          path: '/agent',
          children: [
            {
              path: 'new/:id',
              element: <NewAgentTab />,
            },
            {
              path: ':id',
              element: <AgentTab />,
            },
          ],
        },
        {
          path: '/identities',
          element: <IdentitesPage />,
        },
        {
          path: '/operations',
          element: <OperationsPage />,
        },
        {
          path: '/definition',
          children: [
            {
              path: ':id',
              element: <DefinitionsPage />,
            },
          ],
        },
        {
          path: '/runs/',
          children: [
            {
              path: ':runKey',
              element: <RunHistoryPage />,
            },
          ],
        },
        {
          path: '/run/',
          children: [
            {
              path: ':runKey',
              element: <RunPage />,
              children: [
                {
                  path: ':frameKey',
                  element: <RunPage />,
                },
              ],
            },
          ],
        },
      ]}
    >
      {children}
    </TabManager>
  );
};

export const V0Loading: React.FC = () => {
  return (
    <Center className="h-screen w-full">
      <Text.Title subtitle="Compiling your cotera instance">
        Loading...
      </Text.Title>
      <Loading.Dots />
    </Center>
  );
};

export const V0Router: React.FC = () => {
  return (
    <Suspense fallback={<V0Loading />}>
      <Root />
    </Suspense>
  );
};

const Root: React.FC = () => {
  const client = useTenantedClient();
  const userId = useWhoami((s) => s.whoami.id);
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const tabManager = useTabManager();
  const modalManager = useModalManager((s) => s.actions);
  const db = useAppData((s) => s.initedDb);

  const actions = useMemo(
    () => [
      new NewDatasetAction(tabManager),
      new RunDatasetChangesAction(),
      new PublishGraphAction(client.commits, userId),
      new OpenEditColumnAction(tabManager),
      new RenameDatasetAction(modalManager),
      new FormatAction(),
      new SaveDatasetAction(tabManager, modalManager),
      new CreateChildAction(tabManager, modalManager),
      new DeleteDatasetAction(tabManager, modalManager),
      new RunOnSourceAction({
        queries: queryClient,
        artifacts: client.artifacts,
        orgId,
      }),
      new ToggleEditorAction(),
      new NewColumnAction(tabManager),
      new LLMColumnFilterPreviewAction(),
      new RunAllPreviewsAction(modalManager, db, client.atc),
      new LLMColumnSnapshotAction(client.warehouse, modalManager),
      new CloseTabAction(tabManager),
      new SaveLlmColumnAction(modalManager, tabManager),
      new RunPreviewExpressionColumnAction(),
      new SaveExpressionColumnAction(modalManager),
      new NewDocumentAction(tabManager),
      new SaveDocumentAction(tabManager, modalManager),
      new NewAgentAction(tabManager),
      new SaveAgentAction(tabManager, modalManager),
      new DeleteAgentAction(tabManager, modalManager),
      new RenameAgentAction(tabManager, modalManager),
      new ChangeAgentTabAction(),
      new ApplyTemplateAction(),
      new ScheduleAgentAction(),
      new ChangeAgentModelAction(),
      new NavigateToMentionAction(tabManager, modalManager),
      new VoiceTranscriptionCompleteAction(),
      new SubmitChatMessageAction(),
      new DeleteColumnAction(tabManager, modalManager),
      new SetPrimaryKeyAction(modalManager),
      new SaveTaxonomyColumnAction(modalManager),
      new RunPreviewTaxonomyColumnAction(),
      new EnableAutoPublishAction(),
      new DisableAutoPublishAction(),
      new ClearAllFiltersAction(),
      new RenameColumnAction(modalManager),
      new LlmRunFromStartAction(modalManager),
      new AutoPublishAction(toast),
      new CreateViewAction(modalManager),
      new DownloadCsvAction(db),
      new ToolCallColumnPreviewAction(),
      new SaveToolCallColumnAction(modalManager, tabManager),
      new ToolCallColumnSnapshotAction(client.warehouse, modalManager),
      new ToolCallRunFromStartAction(modalManager),
      new RenameDocumentAction(tabManager, modalManager),
      new DeleteDocumentAction(tabManager, modalManager),
      new NewToolAction(modalManager, tabManager, queryClient, orgId),
      new SelectToolAction(tabManager, queryClient, orgId),
      new SaveToolAction(tabManager, modalManager, queryClient, orgId),
      new RenameToolAction(modalManager),
      new DeleteToolAction(tabManager, modalManager),
      new UpdateCredentialsAction(
        client.tools,
        modalManager,
        queryClient,
        orgId
      ),
      new ExecuteToolAction(client.tools),
      new SubmitToolResultsAction(),
      new ExecuteAllIncompleteToolCallsAction(),
      new ReplayAction(),
      new EditAction(),
      new StopAction(),
      new OpenChatHistoryAction(userId, client.resource.chat, modalManager),
      new ClearChatAction(),
      new LlmRunColumnAction(modalManager),
      new ToolCallRunColumnAction(modalManager),
      new DuplicateAction(modalManager),
      new ConvertToV2Action(queryClient, client.toolsV2, orgId),
      new CsvUploadAction(modalManager),
      new ConfigureTableAction(modalManager, tabManager),
      new ClearChangesAction(),
      new RunAtcCellAction(db, client.atc),
      new OpenBillingSettings(modalManager),
      new OpenSettings(modalManager),
    ],
    [client, userId, queryClient, orgId, tabManager, modalManager, db]
  );

  return (
    <Actions actions={actions}>
      <V0Layout>
        <ErrorBoundary
          fallbackRender={({ error }) => {
            return <DisplayError error={error} />;
          }}
        >
          <Suspense fallback={<LoadingGraph />}>
            <CommandPalette />
            <Routes>
              <Route path="/:orgName/*" element={<Outlet />}>
                <Route path="c">
                  <Route path=":actionName">
                    <Route index element={<ActionRouteRunner />} />
                    <Route path=":jsonParams" element={<ActionRouteRunner />} />
                  </Route>
                </Route>
                <Route
                  path="atc-visibility"
                  element={<ProtectedRoute scopes={[['use', 'superadmin']]} />}
                >
                  <Route index element={<AtcVisibilityPage />} />
                </Route>
                <Route
                  path="bill-engineering-hours"
                  element={<ProtectedRoute scopes={[['use', 'superadmin']]} />}
                >
                  <Route index element={<BillEngineeringHoursPage />} />
                </Route>
                <Route path="unauthorized" element={<UnauthorizedPage />} />
                <Route
                  path="materializations"
                  element={<MaterializationIde />}
                />
                <Route path="artifacts">
                  <Route index element={<Artifacts />} />
                  <Route path=":artifactId" element={<ViewArtifact />} />
                </Route>
                <Route
                  path="*"
                  element={<Tabs fallback={<HomePageFallback />} />}
                />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </V0Layout>
    </Actions>
  );
};

const Tabs: React.FC<{
  fallback?: React.ReactNode;
}> = ({ fallback }) => {
  const { activeTab, renderedTabs } = useTabManager();

  if (activeTab === null) {
    return fallback ?? null;
  }

  return (
    <div className="flex flex-col h-full w-full relative">
      {Object.values(renderedTabs).map((tab) => (
        <div
          key={tab.id}
          className="flex flex-col flex-1 h-full w-full transition-opacity duration-200 ease-in-out w-full"
          style={{
            opacity: tab.id === activeTab ? 1 : 0,
            position: tab.id === activeTab ? 'relative' : 'absolute',
            pointerEvents: tab.id === activeTab ? 'auto' : 'none',
          }}
        >
          {tab.tabContentView}
        </div>
      ))}
    </div>
  );
};

const HomePageFallback: React.FC = () => {
  const model = useInScopeModel(GraphModel);
  const graph = useWatchableValue(model.graph);
  const treeStructure = buildDatasetTree(graph);

  const hasDatasets = treeStructure.topLevelDatasets.length > 0;
  const hasAgents = Object.values(graph.nodeMap).some(
    (node) => node !== null && node.t === 'agent'
  );

  const hasContent = hasDatasets || hasAgents;

  return hasContent ? <HomePageFallbackNormal /> : <HomePageFallbackNewUser />;
};

class HomePageModel extends BaseModel {
  readonly changes: Watchable<Record<string, boolean | string>>;
  readonly route: Watchable<string>;
  readonly pageTitle: Watchable<string>;
  private _isTargeted: boolean = false;

  constructor() {
    super('home-page');
    this.changes = Watchable.fromValue({});
    this.route = Watchable.fromValue('/home');
    this.pageTitle = Watchable.fromValue('Home');
  }

  get isTargeted() {
    return this._isTargeted;
  }

  target() {
    this._isTargeted = true;
  }

  markAsNotTargeted() {
    this._isTargeted = false;
  }
}

const homePageModel = new HomePageModel();

const HomePageTab: React.FC = () => {
  return (
    <AppTab model={homePageModel}>
      <HomePageFallback />
    </AppTab>
  );
};
